// Конструктор процессов: транспорт и формы данных раздела /bpe-defs. ЧТЕНИЕ И ЗАПИСЬ —
// но запись только в СВОЮ схему (таблица bpe_def) через BFF; в Bitrix этим путём не
// пишется ничего, у формата шагов записи в зеркало нет намеренно (ФОРМАТ_ПРОЦЕССОВ.md).
//
// Устроено как api/dq.ts и api/xreports.ts: свой БАЗА, своя функция запроса, хуки рядом.
// Общего клиента здесь нет намеренно — api/client.ts обслуживает эмуляцию Bitrix REST,
// а у модулей ядра форма ответа другая.
//
// ПРОВЕРКА ФОРМЫ ЖИВЁТ И ЗДЕСЬ, И В BFF (bpe_defs_bff.проверить_описание), и это не
// дублирование ради дублирования: клиентская проверка отвечает человеку сразу и по
// месту, серверная — не верит браузеру. Ослабнет одна — держит вторая.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/bpe-defs'

// ─────────────────────────── формы данных (контракт) ───────────────────────────
// Ровно словарь ФОРМАТ_ПРОЦЕССОВ.md. Расширять здесь без правки контракта нельзя.

export type ВидТриггера = 'создание' | 'стадия' | 'поле' | 'срок'
export type Операция = '=' | '!=' | 'пусто' | 'непусто' | '>' | '<'
export type ВидШага = 'задание' | 'уведомление' | 'комментарий'
export type ВидКому = 'роль' | 'bitrix_id' | 'поле_записи'

export const СУЩНОСТИ = ['deal', 'contract', 'invoice', 'payment'] as const

export const ИМЕНА_СУЩНОСТЕЙ: Record<string, string> = {
  deal: 'Сделка', contract: 'Договор', invoice: 'Счёт', payment: 'Платёж',
}

export const ИМЕНА_ТРИГГЕРОВ: Record<ВидТриггера, string> = {
  создание: 'Создание записи',
  стадия: 'Переход в стадию',
  поле: 'Поле приняло значение',
  срок: 'Приближается срок',
}

export const ИМЕНА_ШАГОВ: Record<ВидШага, string> = {
  задание: 'Задание', уведомление: 'Уведомление', комментарий: 'Комментарий',
}

export const ИМЕНА_КОМУ: Record<ВидКому, string> = {
  роль: 'Роль (из BP_ORG)', bitrix_id: 'Сотрудник (Bitrix ID)',
  поле_записи: 'Из поля записи',
}

export interface Кому {
  вид: ВидКому
  значение: string
}

export interface Триггер {
  тип: ВидТриггера
  стадия?: string
  поле?: string
  значение?: string
  срок_дней?: number | ''
}

export interface Условие {
  поле: string
  оп: Операция
  значение?: string
}

export interface Шаг {
  тип: ВидШага
  кому?: Кому
  текст: string
  срок_дней?: number | ''
  канал?: 'tg'
}

export interface Спека {
  триггер: Триггер
  условия: Условие[]
  шаги: Шаг[]
}

/** Строка bpe_def, как её отдаёт ядро. `source` — чей процесс: 'db' правится из
 *  конструктора, 'python' (прошитая спека bp_specs_bitrix) — только просмотр. */
export interface Процесс {
  code: string
  title: string
  entity: string
  enabled: boolean
  source?: string | null
  steps_n?: number | null
  archived_at?: string | null
  updated_by?: string | null
  updated_at?: string | null
  spec?: Спека | null
}

export interface СписокПроцессов {
  rows: Процесс[]
  total?: number
}

/** Что уезжает в POST /save — ровно контракт def-save. */
export interface ТелоСохранения {
  code: string
  title: string
  entity: string
  enabled: boolean
  spec: Спека
}

// ─────────────────────────── транспорт ───────────────────────────

/** Отказ по правам: раздел поимённый (группа 'владелец'). 403 — не поломка, а ответ. */
export class НетДоступа extends Error {
  constructor(msg = 'forbidden') {
    super(msg)
    this.name = 'НетДоступа'
  }
}

/** 400 от BFF/ядра при сохранении: список ошибок формы, по-русски и все разом. */
export class ОшибкаФормы extends Error {
  ошибки: string[]
  constructor(why: string, ошибки: string[]) {
    super(why || 'форма заполнена не до конца')
    this.name = 'ОшибкаФормы'
    this.ошибки = ошибки.length ? ошибки : [why]
  }
}

/** Тот же разбор ответа, что в api/dq.ts; на 400 — ОшибкаФормы со списком. */
async function запрос<T>(путь: string, init?: RequestInit): Promise<T> {
  const res = await fetch(БАЗА + путь, {
    credentials: 'same-origin',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  const rec = (данные || {}) as Record<string, unknown>
  if (res.status === 403) throw new НетДоступа(String(rec.detail ?? rec.error ?? 'forbidden'))
  if (!res.ok) {
    if (res.status === 400) {
      throw new ОшибкаФормы(String(rec.why ?? rec.error ?? ''),
        Array.isArray(rec.errors) ? rec.errors.map(String) : [])
    }
    if (res.status === 501) throw new CoreUnsupported(String(rec.why ?? ''))
    const msg = (rec.why as string) || (rec.detail as string) || (rec.error as string)
      || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

/** Карточка может приехать как {row}, {def} или плоско — ядро делает параллельный
 *  агент, и до первой встречи форм мы принимаем любую из трёх, а не падаем. */
export function строкаКарточки(данные: unknown): Процесс {
  const rec = (данные || {}) as Record<string, unknown>
  const тело = (rec.row ?? rec.def ?? rec) as Процесс
  return тело
}

/** Сколько шагов у процесса: счётчик ядра, а если его нет — длина спеки. */
export function шаговУ(п: Процесс): number | null {
  if (typeof п.steps_n === 'number') return п.steps_n
  if (п.spec && Array.isArray(п.spec.шаги)) return п.spec.шаги.length
  return null
}

export const списокПроцессов = () => запрос<СписокПроцессов>('/list')

export const карточкаПроцесса = (code: string) =>
  запрос<unknown>('/def?code=' + encodeURIComponent(code)).then(строкаКарточки)

export const сохранитьПроцесс = (тело: ТелоСохранения) =>
  запрос<{ ok?: boolean; code?: string }>('/save', {
    method: 'POST', body: JSON.stringify(тело),
  })

export const архивироватьПроцесс = (code: string) =>
  запрос<{ ok?: boolean }>('/archive', {
    method: 'POST', body: JSON.stringify({ code }),
  })

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export const useПроцессы = () =>
  useQuery({ queryKey: ['bpe-defs', 'list'], queryFn: списокПроцессов, ...ОБЩЕЕ })

export const useПроцесс = (code: string | null) =>
  useQuery({
    queryKey: ['bpe-defs', 'def', code],
    queryFn: () => карточкаПроцесса(code ?? ''),
    enabled: !!code,
    ...ОБЩЕЕ,
  })

// ─────────────────────────── проверка формы на клиенте ───────────────────────────
// Зеркало bpe_defs_bff.проверить_описание: те же правила, те же слова. Сервер всё
// равно проверит сам — здесь проверка ради мгновенного ответа человеку.

const КОД = /^[a-z][a-z0-9_-]{1,63}$/
const СУЩНОСТЬ_ITEM = /^item:\d+$/

const целое = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null
  const n = Number(v)
  return Number.isInteger(n) ? n : null
}

const непусто = (v: unknown): boolean => String(v ?? '').trim() !== ''

function проверитьКому(кому: Кому | undefined, куда: string, ошибки: string[]) {
  if (!кому || !['роль', 'bitrix_id', 'поле_записи'].includes(кому.вид)) {
    ошибки.push(`${куда}: не выбрано, кому`)
    return
  }
  if (!непусто(кому.значение)) ошибки.push(`${куда}: не заполнено, кому именно`)
}

/** Все ошибки формы разом, по-русски. Пусто = сохранять можно. */
export function проверитьФорму(тело: ТелоСохранения): string[] {
  const ошибки: string[] = []
  if (!КОД.test(тело.code)) {
    ошибки.push('код: латиницей, строчными, начинается с буквы '
      + '(буквы, цифры, «-», «_», 2–64 знака)')
  }
  if (!непусто(тело.title)) ошибки.push('название: не заполнено')
  if (!(СУЩНОСТИ as readonly string[]).includes(тело.entity)
    && !СУЩНОСТЬ_ITEM.test(тело.entity)) {
    ошибки.push('сущность: одна из ' + СУЩНОСТИ.join(', ') + ' или item:<номер типа>')
  }

  const т = тело.spec.триггер
  if (!т || !['создание', 'стадия', 'поле', 'срок'].includes(т.тип)) {
    ошибки.push('триггер: не выбран тип')
  } else {
    if (т.тип === 'стадия' && !непусто(т.стадия)) {
      ошибки.push('триггер «стадия»: не назван код стадии')
    }
    if ((т.тип === 'поле' || т.тип === 'срок') && !непусто(т.поле)) {
      ошибки.push(`триггер «${т.тип}»: не назван код поля`)
    }
    if (т.тип === 'поле' && !непусто(т.значение)) {
      ошибки.push('триггер «поле»: не сказано, какое значение ждать')
    }
    if (т.тип === 'срок') {
      const дней = целое(т.срок_дней)
      if (дней === null || дней <= 0) {
        ошибки.push('триггер «срок»: срок в днях — целое число больше нуля')
      }
    }
  }

  тело.spec.условия.forEach((у, i) => {
    if (!непусто(у.поле)) ошибки.push(`условие ${i + 1}: не названо поле`)
    if (!['=', '!=', 'пусто', 'непусто', '>', '<'].includes(у.оп)) {
      ошибки.push(`условие ${i + 1}: не выбрана операция`)
    } else if (у.оп !== 'пусто' && у.оп !== 'непусто' && !непусто(у.значение)) {
      ошибки.push(`условие ${i + 1}: не заполнено значение`)
    }
  })

  if (тело.enabled && тело.spec.шаги.length === 0) {
    ошибки.push('включить процесс без единого шага нельзя — сохраните выключенным '
      + 'или добавьте шаг')
  }
  тело.spec.шаги.forEach((ш, i) => {
    const куда = `шаг ${i + 1} (${ш.тип})`
    if (!непусто(ш.текст)) ошибки.push(`${куда}: не заполнен текст`)
    if (ш.тип === 'задание') {
      проверитьКому(ш.кому, куда, ошибки)
      if (ш.срок_дней !== undefined && ш.срок_дней !== '' && ш.срок_дней !== null) {
        const дней = целое(ш.срок_дней)
        if (дней === null || дней <= 0) {
          ошибки.push(`${куда}: срок в днях — целое число больше нуля`)
        }
      }
    }
    if (ш.тип === 'уведомление') проверитьКому(ш.кому, куда, ошибки)
  })
  return ошибки
}
